// The chat box's mic (TOM-671): the browser's own dictation (the Web Speech
// API). Chrome, Edge and Safari have it; Firefox does not, and there the mic
// is not shown. What the owner says is written into the text field as they
// speak, after whatever was already typed, and the field stays theirs to edit
// before they send. Nothing is sent by speaking.
//
// Chrome sends the audio to Google's speech service to turn it into words;
// Safari uses Apple's. The text field shows the words; Neomaris never sees the
// audio.

import { useCallback, useEffect, useRef, useState } from 'react';

// The parts of the Web Speech API used here. TypeScript's DOM library does
// not declare it, and Chrome and Safari still name it with a webkit prefix.
interface SpeechAlternative {
  transcript: string;
}
interface SpeechResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechAlternative;
}
interface SpeechResultEvent {
  readonly resultIndex: number;
  readonly results: { readonly length: number; [index: number]: SpeechResult };
}
interface SpeechErrorEvent {
  readonly error: string;
}
interface SpeechRecognizer {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onerror: ((event: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type SpeechRecognizerClass = new () => SpeechRecognizer;

function recognizerClass(): SpeechRecognizerClass | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognizerClass;
    webkitSpeechRecognition?: SpeechRecognizerClass;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** What the box says when the mic heard nothing at all. */
export const DICTATION_HEARD_NOTHING =
  'The mic didn’t hear anything. Check which microphone your browser is using.';

/** Listening this long with nothing heard and no error says so when it stops. */
const DICTATION_HEARD_NOTHING_MS = 5_000;

/**
 * The line for a dictation error (the Web Speech API's `error` codes). Every
 * error but `aborted` (the owner's own stop) says something (TOM-731).
 */
export function dictationErrorLine(error: string): string {
  if (error === 'not-allowed' || error === 'service-not-allowed') {
    return 'The browser didn’t allow the microphone. Allow it in the site settings to dictate.';
  }
  if (error === 'audio-capture') {
    return 'The browser can’t hear your microphone. Check which microphone it’s using.';
  }
  if (error === 'no-speech') return DICTATION_HEARD_NOTHING;
  return 'Dictation stopped. Try the mic again.';
}

/** The typed text with the spoken words after it, one space between. */
export function joinDictation(typed: string, spoken: string): string {
  const said = spoken.trim();
  if (said.length === 0) return typed;
  if (typed.length === 0) return said;
  return /\s$/.test(typed) ? `${typed}${said}` : `${typed} ${said}`;
}

export interface Dictation {
  /** False where the browser cannot dictate: the mic is not shown. */
  supported: boolean;
  listening: boolean;
  /** A sentence when the mic could not start, such as permission refused. */
  error: string | null;
  toggle: () => void;
  stop: () => void;
}

/**
 * Dictation into a text field whose value is `text`, written with `setText`.
 */
export function useDictation(text: string, setText: (next: string) => void): Dictation {
  const [supported] = useState(() => recognizerClass() !== null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognizerRef = useRef<SpeechRecognizer | null>(null);
  const textRef = useRef(text);
  textRef.current = text;
  const setTextRef = useRef(setText);
  setTextRef.current = setText;

  const stop = useCallback(() => {
    recognizerRef.current?.stop();
  }, []);

  useEffect(() => () => recognizerRef.current?.abort(), []);

  const start = useCallback(() => {
    const Recognizer = recognizerClass();
    if (Recognizer === null) return;
    const recognizer = new Recognizer();
    recognizer.lang = navigator.language || 'en-US';
    recognizer.continuous = true;
    recognizer.interimResults = true;
    // What was typed before the mic started; the words heard so far go after it.
    const before = textRef.current;
    let finals = '';
    // Whether anything was heard, or said why not, before the mic stopped.
    let heard = false;
    let said = false;
    const startedAt = Date.now();
    recognizer.onresult = (event) => {
      heard = true;
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i]!;
        const said = result[0]?.transcript ?? '';
        if (result.isFinal) finals = joinDictation(finals, said);
        else interim = joinDictation(interim, said);
      }
      setTextRef.current(joinDictation(before, joinDictation(finals, interim)));
    };
    recognizer.onerror = (event) => {
      if (event.error === 'aborted') return;
      said = true;
      setError(dictationErrorLine(event.error));
    };
    recognizer.onend = () => {
      // Listened a while and heard nothing, with no error: never a silent stop
      // (TOM-731: Mike's Chrome captured nothing and the mic just went off).
      if (!heard && !said && Date.now() - startedAt >= DICTATION_HEARD_NOTHING_MS) {
        setError(DICTATION_HEARD_NOTHING);
      }
      if (recognizerRef.current === recognizer) recognizerRef.current = null;
      setListening(false);
    };
    recognizerRef.current = recognizer;
    setError(null);
    try {
      recognizer.start();
      setListening(true);
    } catch {
      recognizerRef.current = null;
      setError('Dictation couldn’t start. Try the mic again.');
    }
  }, []);

  const toggle = useCallback(() => {
    if (recognizerRef.current !== null) stop();
    else start();
  }, [start, stop]);

  return { supported, listening, error, toggle, stop };
}
